import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useParams } from "@tanstack/react-router";
import {
  Heart,
  Menu,
  Moon,
  PanelLeftClose,
  Sun,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { api } from "../api";
import { dashboardNav, extraNav, modules } from "../config";
import { AiAssistant } from "./AiAssistant";

const navGroups = [
  { label: "总览与计划", paths: ["dashboard", "tasks", "timeline", "budget"] },
  {
    label: "供应商与物料",
    paths: ["vendors", "core-vendors", "decorations", "stationery-gifts"],
  },
  { label: "宾客与座位", paths: ["guests", "seating"] },
  {
    label: "婚礼当天",
    paths: ["day-schedule", "shots", "packing", "assignments", "risks"],
  },
  { label: "灵感", paths: ["ideas"] },
  { label: "项目管理", paths: ["audit", "settings"] },
];

export function AppShell() {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId?: string;
  };
  const [collapsed, setCollapsed] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [pending, setPending] = useState(0);
  const [dark, setDark] = useState(
    () => localStorage.getItem("theme") === "dark",
  );
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const workspace = useQuery({
    queryKey: [workspaceId, "workspace"],
    queryFn: () => api.workspace(workspaceId!).then((result) => result.data),
    enabled: Boolean(workspaceId),
  });

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
  }, [dark]);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const update = () => {
      setIsNarrow(media.matches);
      if (!media.matches) setMobile(false);
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    const sync = (event: Event) =>
      setPending(
        Number(
          (event as CustomEvent<{ pending?: number }>).detail?.pending ?? 0,
        ),
      );
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    window.addEventListener("wedding-sync-status", sync);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      window.removeEventListener("wedding-sync-status", sync);
    };
  }, []);
  useEffect(() => {
    if (!isNarrow || !mobile) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    sidebarRef.current?.querySelector<HTMLAnchorElement>("a")?.focus();
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobile(false);
        menuButtonRef.current?.focus();
      }
      if (event.key === "Tab" && sidebarRef.current) {
        const focusable = [
          ...sidebarRef.current.querySelectorAll<HTMLElement>(
            "a, button:not(:disabled)",
          ),
        ];
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", close);
    };
  }, [isNarrow, mobile]);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };
  const nav = useMemo(() => [dashboardNav, ...modules, ...extraNav], []);
  const navByPath = useMemo(
    () => Object.fromEntries(nav.map((item) => [item.path, item])),
    [nav],
  );
  const weddingDate = workspace.data?.settings.weddingDate;
  const syncLabel = !online ? "离线" : pending ? `${pending} 项待同步` : "在线";

  return (
    <div className={`app-shell ${collapsed ? "collapsed" : ""}`}>
      {isNarrow && mobile && (
        <button
          className="mobile-nav-backdrop"
          aria-label="关闭菜单"
          onClick={() => {
            setMobile(false);
            menuButtonRef.current?.focus();
          }}
        />
      )}
      <aside
        ref={sidebarRef}
        className={mobile ? "open" : ""}
        aria-hidden={isNarrow && !mobile}
        inert={isNarrow && !mobile}
      >
        <div className="brand">
          <span className="brand-mark">囍</span>
          <div>
            <strong>知禧</strong>
            <small>婚礼工作台</small>
          </div>
          <button
            onClick={() => {
              if (isNarrow) {
                setMobile(false);
                menuButtonRef.current?.focus();
              } else setCollapsed(!collapsed);
            }}
            aria-label={
              isNarrow ? "关闭菜单" : collapsed ? "展开菜单" : "折叠菜单"
            }
          >
            {isNarrow ? <X size={20} /> : <PanelLeftClose size={18} />}
          </button>
        </div>
        <nav aria-label="婚礼项目导航">
          {navGroups.map((group) => (
            <section className="nav-group" key={group.label}>
              <span className="nav-group-label">{group.label}</span>
              {group.paths.map((path) => {
                const item = navByPath[path];
                if (!item) return null;
                const Icon = item.icon;
                return (
                  <Link
                    key={path}
                    to="/w/$workspaceId/$section"
                    params={{ workspaceId: workspaceId!, section: path }}
                    activeProps={{ className: "active" }}
                    onClick={() => setMobile(false)}
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </section>
          ))}
        </nav>
        <div className="sidebar-foot">
          <Link to="/workspaces" onClick={() => setMobile(false)}>
            <Heart size={17} />
            <span>切换婚礼项目</span>
          </Link>
        </div>
      </aside>
      <div className="main-column">
        <header className="topbar">
          <button
            ref={menuButtonRef}
            className="mobile-menu"
            onClick={() => setMobile(!mobile)}
            aria-label={mobile ? "关闭菜单" : "打开菜单"}
            aria-expanded={mobile}
          >
            <Menu />
          </button>
          <div className="project-crumb">
            <span>
              {weddingDate
                ? weddingDate.slice(0, 7).replace("-", " / ")
                : "婚礼项目"}
            </span>
            <strong>
              {workspace.data?.settings.projectName ?? "婚礼项目"}
            </strong>
          </div>
          <div className="top-actions">
            <span className={`network ${online ? "online" : "offline"}`}>
              {online ? <Wifi size={15} /> : <WifiOff size={15} />}
              <em>{syncLabel}</em>
            </span>
            <button onClick={toggleTheme} aria-label="切换深色模式">
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <span className="avatar" title="当前婚礼项目">
              婚礼
            </span>
          </div>
        </header>
        <main>
          <Outlet />
        </main>
      </div>
      {workspaceId && <AiAssistant workspaceId={workspaceId} />}
    </div>
  );
}
